import { formatUnits } from "viem";

export function shortAddress(value: string, lead = 6, tail = 4): string {
  if (value.length <= lead + tail + 1) return value;
  return `${value.slice(0, lead)}...${value.slice(-tail)}`;
}

/** Whole-number display for counts such as eligible wallets and registrations. */
export function formatCount(value: number): string {
  return new Intl.NumberFormat("en-US").format(value);
}

/** $INDEXSY balances are plain decimal numbers in the snapshot. */
export function formatBalance(value: number): string {
  return new Intl.NumberFormat("en-US", { maximumFractionDigits: 2 }).format(value);
}

export function formatShare(share: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "percent",
    minimumFractionDigits: 2,
    maximumFractionDigits: 4,
  }).format(share);
}

/** Token amounts arrive as wei strings. */
export function formatTokenAmount(wei: string, decimals: number): string {
  try {
    const whole = formatUnits(BigInt(wei), decimals);
    const asNumber = Number(whole);
    if (!Number.isFinite(asNumber)) return whole;
    return new Intl.NumberFormat("en-US", { maximumFractionDigits: 4 }).format(asNumber);
  } catch {
    return wei;
  }
}

/** Numbers below this are unix seconds, above are milliseconds. */
const MS_THRESHOLD = 1e12;

export function formatDateTime(value: string | number | null): string {
  if (value === null || value === "") return "not set";
  const date =
    typeof value === "number"
      ? new Date(value < MS_THRESHOLD ? value * 1000 : value)
      : new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    timeZoneName: "short",
  });
}

/** Registration timestamps may be unix seconds or an ISO string. */
export function formatRegisteredAt(value: number | string): string {
  return formatDateTime(value);
}

export function formatTimeLeft(unixSeconds: number, now: number = Date.now()): string {
  const ms = unixSeconds * 1000 - now;
  if (ms <= 0) return "closed";
  const minutes = Math.floor(ms / 60000);
  const days = Math.floor(minutes / 1440);
  const hours = Math.floor((minutes % 1440) / 60);
  if (days > 0) return `${days}d ${hours}h left`;
  if (hours > 0) return `${hours}h ${minutes % 60}m left`;
  return `${minutes}m left`;
}
